import { useState, useEffect, useLayoutEffect } from 'react';
import { animated, useSpring } from "react-spring";
import { useSelector, useDispatch } from 'react-redux'
import { updatePlayers, setGameStatus, updateReadyState, setGameFaker, setSecretWord, setConfig } from './redux/gameState';
import { setPlayerId, initPlayerState } from './redux/playerState';
import { initConfigState } from './redux/configState';
import './App.css';
import GameConfig from './GameConfig';
import Players from './Players';

function Lobby(props) {
  const [inputState, setInputState] = useState("disabled");
  const [socket] = useState(props.socket);
  const [settingsDisplay, setSettingsDisplay] = useState("hidden");
  const [currentSpring, setSpring] = useState({});
  const ps = useSelector((state) => state.playerState);

  const dispatch = useDispatch();
  const configState = useSelector((state) => state.configState);
  const gameState = useSelector((state) => state.gameState);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // todo: refactor to use single spring with toggle state
  useLayoutEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };
    setSpring(lobbyPos);
    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ----------- lobby springs
  function lobbyLeave() {
    LobbyLeaveApi.start({
      y: viewportHeight + 1000,
      rotateZ: 45
    });
  }

  const [lobbyPos, lobbyEnterApi] = useSpring(() => ({
    display: "block",
    from: {
      y: viewportHeight - 2000,
      rotateZ: -45,
      display: "none",
    },
    config: {
      duration: 500,
    },
    delay: 500,
    onRest: () => setSpring(lobbyLeavePos),
  }))

  const [lobbyLeavePos, LobbyLeaveApi] = useSpring(() => ({
    onStart: () => props.lobbyLeave(true),
    from: {
      y: 0,
      rotateZ: 0,
    },
    config: {
      duration: 800,
    },
    delay: 500,
    onRest: () => props.gameStart(true),
  }))

  const handleSubmit = (evt) => {
    evt.preventDefault();
    if (gameState.players.length <= 1) {
      return;
    }
    console.log("gameState on ready: ", gameState);
    lobbyLeave();
    dispatch(updateReadyState(true));
    dispatch(setConfig(configState));
    let roomNum = gameState.room;
    console.log("readying up room# ", roomNum);
    socket.emit("updateConfig", (configState));
    socket.emit("setReady", (roomNum));
    socket.emit("setFaker", (gameState));
  }

  const openSettings = (evt) => {
    evt.preventDefault();
    setSettingsDisplay("block");
    configSpringCallback();
  }

  useEffect(() => {
    lobbyEnterApi.start({
      y: 0,
      rotateZ: 0,
      display: "block",
    });
  }, [lobbyEnterApi]);

  // this is received like a million times
  useEffect(() => {
    if (!socket) return;
    socket.once("updatePlayers", updatedPlayers => {
      dispatch(initPlayerState(updatedPlayers.find(p => p.id === ps.id)));
      dispatch(updatePlayers(updatedPlayers));
    });
    return () => {
      socket.off('updatePlayers');
    }
  }, [socket, dispatch, ps.id]);

  useEffect(() => {
    if (!socket) return;
    socket.once("updatePlayerId", playerId => {
      dispatch(setPlayerId(playerId));
    });
    return () => {
      socket.off('updatePlayerId');
    }
  }, [socket, dispatch]);

  useEffect(() => {
    if (!socket) return;
    socket.on("gameFull", gState => {
      dispatch(setGameStatus("full"));
    });
    return () => {
      socket.off('gameFull');
    }
  }, [socket, dispatch]);

  useEffect(() => {
    if (!socket) return;
    socket.on("gameReady", (rdyState) => {
      lobbyLeave();
      setTimeout(() => { props.gameStart(true) }, 300);
      dispatch(updateReadyState(rdyState))
    });
    return () => {
      socket.off('gameReady');
    }
  }, [socket, dispatch]);

  useEffect(() => {
    if (configState != null && gameState.players.length >= configState.minPlayers && ps.host) {
      setInputState('');
    }
    else {
      setInputState('disabled')
    }
  }, [gameState.players.length, configState, ps.host]);

  useEffect(() => {
    if (!socket) return;
    socket.once("updateFaker", (fakePlayer) => {
      dispatch(setGameFaker(fakePlayer));
    });
    return () => {
      socket.off('updateFaker');
    };
  }, [socket, dispatch]);

  useEffect(() => {
    if (!socket) return;
    socket.once("setWord", (word) => {
      dispatch(setSecretWord(word));
    });
    return () => {
      socket.off('setWord');
    };
  }, [socket, dispatch]);

  //prob a better way to do this than passing function as prop. Not as easy to reuse elsewhere
  function handleClose(close) {
    configClose();
  }

  useEffect(() => {
    if (!socket) return;
    socket.once("setConfig", (cfg) => {
      if (!ps.host) {
        dispatch(initConfigState(cfg));
        dispatch(setConfig(cfg));
      }
    });
    return () => {
      socket.off('setConfig');
    };
  }, [socket, dispatch, ps.host]);

  // ---------------- config springs

  function configSpringCallback() {
    configOpenApi.start({
      y: configOpenPos.y.get() === viewportHeight + 1200 ? 0 : viewportHeight + 1200,
    });
  }

  function configClose() {
    configOpenApi.start({
      y: configOpenPos.y.get() === 0 ? viewportHeight + 1200 : 0,
    });
  }

  const [configOpenPos, configOpenApi] = useSpring(() => ({
    from: {
      y: viewportHeight + 1200,
    },
    config: { duration: 500 },
  }))

  return (
    <>
      <animated.div style={{ ...configOpenPos }} className={settingsDisplay + " mt-60 z-20 absolute rounded rounded-sm block fade bg-white shadow-2xl shadow-lg sm:p-5 text-base text-gray-700 text-xl grid place-content-center mx-auto"}>
        <GameConfig ps={ps} socket={socket} handleClose={(close) => handleClose(close)} />
      </animated.div>
      <div className='z-10 col-span-12 grid grid-cols-12 relative w-full overflow-hidden flex md:items-center md:justify-center'>
        <animated.div className="App col-span-12 md:col-span-8 col-start-1 2xl:col-span-4 2xl:col-start-5 lg:text-2xl md:text-md text-sm md:mx-6 max-w-full place-content-end " style={{ ...currentSpring }}>
          <div className=" bg-gray-100 shadow-2xl md:p-10 md:px-4 p-2 sm:py-10 rounded-sm bottom-0 max-w-full place-content-end max-h-full">
            <div className="place-content-end">
              <h3>Room number: {gameState.room} waiting room</h3>
              <form onSubmit={handleSubmit}>
                <span>
                  <label>Click when all players have entered (minimum {configState.minPlayers})</label>
                  <div>
                    <button onClick={(evt) => handleSubmit(evt)} type="submit" value="Submit" disabled={inputState}
                      className={((inputState === "disabled") ? 'disabled:opacity-50 ' : '') + "m-3 bg-green-500 hover:bg-green-700 font-bold py-2 px-4 rounded"}>
                      Begin
                    </button>
                    <button onClick={(evt) => openSettings(evt)} className="m-3 bg-green-500 hover:bg-green-500 font-bold py-2 px-4 rounded">
                      Config
                    </button>
                  </div>
                </span>
              </form>
              <Players
                gameState={gameState}
                currentTurn={gameState.turn}
                players={gameState.players}
                player={ps}
                socket={socket}
                lobby={props.lobby}
              />
            </div>
          </div>
        </animated.div>
        {props.children}
      </div>
    </>
  );
}

export default Lobby;
